import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, LogOut, User, Mail, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";

export default function Account() {
  const { user, setAuthOpen, logout, googleLogin } = useAuth();
  const { format } = useCurrency();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user) api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {});
  }, [user]);

  if (user === null) return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-[#00F0FF] border-t-transparent" /></div>;

  if (!user) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00F0FF]/10"><User className="h-8 w-8 text-[#00F0FF]" /></div>
        <h1 className="font-display mt-6 text-2xl font-bold text-white">Sign in to your account</h1>
        <p className="mt-2 text-slate-400">Access your orders, wishlists and saved addresses.</p>
        <div className="mt-6 flex flex-col gap-3">
          <Button onClick={() => setAuthOpen(true)} className="rounded-xl bg-[#00F0FF] py-6 font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="account-signin-btn">Sign In / Register</Button>
          <Button onClick={googleLogin} variant="outline" className="rounded-xl border-white/15 bg-white/5 py-6 text-slate-100 hover:bg-white/10" data-testid="account-google-btn">Continue with Google</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="dw-card flex flex-col items-start justify-between gap-4 rounded-2xl p-6 sm:flex-row sm:items-center">
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="h-14 w-14 rounded-full" /> : <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#00F0FF] to-[#8A2BE2] font-display text-xl font-black text-[#08090E]">{(user.name || user.email)[0]?.toUpperCase()}</div>}
          <div>
            <h1 className="font-display text-xl font-bold text-white">{user.name || "Welcome back"}</h1>
            <p className="flex items-center gap-1.5 text-sm text-slate-400"><Mail className="h-3.5 w-3.5" /> {user.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {["owner", "admin", "manager"].includes(user.role) && <Link to="/admin"><Button className="rounded-xl bg-[#8A2BE2] text-white hover:bg-[#9d4ce8]" data-testid="account-admin-btn"><ShieldCheck className="mr-2 h-4 w-4" /> Admin</Button></Link>}
          <Button onClick={logout} variant="outline" className="rounded-xl border-white/15 bg-transparent text-slate-200 hover:bg-white/5" data-testid="account-logout-btn"><LogOut className="mr-2 h-4 w-4" /> Sign Out</Button>
        </div>
      </div>

      <h2 className="font-display mt-10 flex items-center gap-2 text-xl font-bold text-white"><Package className="h-5 w-5 text-[#00F0FF]" /> Order History</h2>
      {orders.length === 0 ? (
        <div className="dw-card mt-4 rounded-2xl p-12 text-center text-slate-400" data-testid="account-no-orders">
          No orders yet. <Link to="/shop" className="text-[#00F0FF]">Start shopping</Link>
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="account-orders">
          {orders.map((o) => (
            <Link key={o.order_id} to={`/track?order=${o.order_id}`} className="dw-card flex items-center justify-between rounded-2xl p-5" data-testid={`account-order-${o.order_id}`}>
              <div>
                <p className="font-mono text-sm text-[#00F0FF]">#{o.order_id}</p>
                <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()} · {o.items?.length} item(s)</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm font-bold text-white">{format(o.total_usd)}</p>
                <span className="rounded-full bg-[#00F0FF]/10 px-2 py-0.5 font-mono text-[10px] uppercase text-[#00F0FF]">{o.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
